import { useEffect, useMemo, useState } from "react";
import usePagination, { paginationRangeLabel, paginationSx } from "../../../hooks/usePagination";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getClassDetails, getClassStudents } from "../../../redux/sclassRelated/sclassHandle";
import { deleteUser } from "../../../redux/userRelated/userHandle";
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Fade,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Pagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { resolveUploadedMediaUrl } from "../../../config/api";

const headCells = [
    { id: "name", label: "Student", width: "30%" },
    { id: "rollNum", label: "Roll no.", width: "12%" },
    { id: "status", label: "Status", width: "14%" },
    { id: "feeBalance", label: "Fee balance", width: "16%" },
    { id: "actions", label: "", align: "right", width: "28%" },
];

function TableSkeleton() {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headCells.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 600 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={5}>
                                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function RowActions({ row, onDelete }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <Tooltip title="View profile" arrow>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigate("/Admin/students/student/" + row.id)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                    }}
                >
                    <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="View attendance" arrow>
                <IconButton
                    size="small"
                    onClick={() => navigate("/Admin/students/student/attendance/" + row.id)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.info.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.info.main, 0.16) },
                    }}
                >
                    <FactCheckOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="View marks" arrow>
                <IconButton
                    size="small"
                    onClick={() => navigate("/Admin/students/student/marks/" + row.id)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.success.main, 0.16) },
                    }}
                >
                    <GradingOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="More" arrow>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onDelete(row);
                    }}
                    sx={{ color: "error.main", gap: 1 }}
                >
                    <PersonRemoveIcon fontSize="small" />
                    Remove student
                </MenuItem>
            </Menu>
        </Stack>
    );
}

const ClassStudents = () => {
    const { classID } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { sclassStudents, sclassDetails, loading, error, getresponse } = useSelector((state) => state.sclass);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (classID) {
            dispatch(getClassDetails(classID, "Sclass"));
            dispatch(getClassStudents(classID));
        }
    }, [classID, dispatch]);

    const className = sclassDetails?.sclassName || "Class";
    const enrolled = sclassDetails?._count?.students ?? (Array.isArray(sclassStudents) ? sclassStudents.length : 0);
    const capacity = sclassDetails?.studentCapacity;
    const capLabel =
        capacity != null && capacity > 0 ? `${enrolled} / ${capacity}` : `${enrolled} enrolled`;

    const rows = useMemo(() => {
        if (!Array.isArray(sclassStudents) || sclassStudents.length === 0) return [];
        return sclassStudents.map((student) => {
            const f = student.feeBalance;
            const feeNum = f === undefined || f === null ? null : typeof f === "number" ? f : parseFloat(String(f), 10);
            const feeLabel =
                feeNum !== null && !Number.isNaN(feeNum)
                    ? feeNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "—";
            const photo = resolveUploadedMediaUrl(student.photoUrl) || null;
            const initial = (student.name || "?").charAt(0).toUpperCase();
            return {
                name: student.name,
                rollNum: student.rollNum,
                admissionStatus: student.admissionStatus || "—",
                feeLabel,
                photo,
                initial,
                id: student._id,
            };
        });
    }, [sclassStudents]);

    const pageSize = 30;
    const { page, setPage, pageCount, paginatedItems, totalItems } = usePagination(rows, pageSize);

    const refresh = () => {
        if (classID) {
            dispatch(getClassStudents(classID));
            dispatch(getClassDetails(classID, "Sclass"));
        }
    };

    const [deleteDialog, setDeleteDialog] = useState(null);

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteStudentDialog = (row) => {
        setDeleteDialog({
            title: `Delete student: ${row.name}`,
            description:
                "This will permanently delete this student and their fee records, attendance, and marks.",
            confirmWord: row.name,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(row.id, "Student"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage(outcome?.error || "Could not remove this student.");
                setShowPopup(true);
            },
        });
    };

    const openDeleteAllInClassDialog = () => {
        setDeleteDialog({
            title: `Delete all students in ${className}`,
            description: "This will permanently delete every student in this class. This cannot be undone.",
            confirmWord: "DELETE ALL STUDENTS",
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(classID, "StudentsClass"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage(outcome?.error || "Could not delete students in this class.");
                setShowPopup(true);
            },
        });
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows;
    const showTable = !loading && !error && hasRows;

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Button
                            startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => navigate("/Admin/students")}
                            sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                        >
                            Back to classes
                        </Button>

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(
                                        t.palette.primary.light,
                                        0.95
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.primary.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack spacing={0.75}>
                                    <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                        Class roster
                                    </Typography>
                                    <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                        {className}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 520 }}>
                                        Students in this class — profiles, attendance, marks, and report cards.
                                    </Typography>
                                    <Chip
                                        label={capLabel}
                                        size="small"
                                        sx={{
                                            alignSelf: "flex-start",
                                            mt: 0.5,
                                            bgcolor: alpha("#fff", 0.2),
                                            color: "common.white",
                                            fontWeight: 700,
                                        }}
                                    />
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PersonAddAlt1Icon />}
                                        onClick={() => navigate("/Admin/class/addstudents/" + classID)}
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                            },
                                        }}
                                    >
                                        Add student
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        color="inherit"
                                        startIcon={<PersonRemoveIcon />}
                                        onClick={openDeleteAllInClassDialog}
                                        disabled={!hasRows}
                                        sx={{
                                            borderColor: alpha("#fff", 0.55),
                                            color: "common.white",
                                            borderRadius: 2,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                bgcolor: alpha("#fff", 0.08),
                                            },
                                        }}
                                    >
                                        Delete all in class
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load students."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box sx={{ py: { xs: 6, sm: 8 }, px: 2, textAlign: "center" }}>
                                        <GroupsOutlinedIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
                                        <Typography variant="h6" gutterBottom fontWeight={600}>
                                            No students in {className}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto", mb: 3 }}>
                                            {typeof getresponse === "string"
                                                ? getresponse
                                                : "Enrol learners into this class to manage attendance, marks, and fees."}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<PersonAddAlt1Icon />}
                                            onClick={() => navigate("/Admin/class/addstudents/" + classID)}
                                            sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                                        >
                                            Add student to {className}
                                        </Button>
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <Box
                                            sx={{
                                                px: { xs: 2, sm: 2.5 },
                                                py: 2,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Students
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={`${rows.length} in class`}
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                {paginationRangeLabel(page, pageSize, totalItems, "students")}
                                            </Typography>
                                        </Box>
                                        <TableContainer>
                                            <Table size="medium" aria-label="class students table">
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((col) => (
                                                            <TableCell
                                                                key={col.id}
                                                                align={col.align || "left"}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: 12,
                                                                    letterSpacing: 0.6,
                                                                    textTransform: "uppercase",
                                                                    color: "text.secondary",
                                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                                                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                                    py: 2,
                                                                    width: col.width,
                                                                }}
                                                            >
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedItems.map((row) => (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            sx={{
                                                                "&:hover": {
                                                                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                                    <Avatar
                                                                        src={row.photo || undefined}
                                                                        sx={{ width: 40, height: 40, fontWeight: 700 }}
                                                                    >
                                                                        {!row.photo ? row.initial : null}
                                                                    </Avatar>
                                                                    <Typography fontWeight={600}>{row.name}</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip
                                                                    label={String(row.rollNum)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 600 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip
                                                                    label={row.admissionStatus}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                                    {row.feeLabel}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ py: 2 }}>
                                                                <RowActions row={row} onDelete={openDeleteStudentDialog} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {pageCount > 1 && (
                                            <Pagination
                                                count={pageCount}
                                                page={page}
                                                onChange={(_, v) => setPage(v)}
                                                color="primary"
                                                sx={paginationSx}
                                            />
                                        )}
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
        </Box>
    );
};

export default ClassStudents;
